import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  EyeIcon,
  FunnelIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';

export default function CategoryPage() {
  const { slug } = useParams(); // Obtener el slug de la categoría desde la URL
  const [articles, setArticles] = useState([]);
  const [category, setCategory] = useState(null);
  const [categories, setCategories] = useState([]);
  const [expandedCategories, setExpandedCategories] = useState([]);
  const [recentArticles, setRecentArticles] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortOption, setSortOption] = useState('recent');
  const [showSortOptions, setShowSortOptions] = useState(false);
  const [loading, setLoading] = useState(true);
  const articlesPerPage = 12;

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch articles by category slug
        const articlesRes = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/articles/category/${slug}/`
        );
        if (!articlesRes.ok)
          throw new Error('Error fetching articles by category');
        const articlesData = await articlesRes.json();

        const published = articlesData.filter((a) => a.status === 'published');
        published.sort(
          (a, b) => new Date(b.published_at) - new Date(a.published_at)
        );
        setArticles(published);

        setRecentArticles(published.slice(0, 5));

        // Fetch category info
        const categoryRes = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/categories/slug/${slug}/`
        );
        if (categoryRes.ok) {
          const categoryData = await categoryRes.json();
          setCategory(categoryData);
        }

        // Fetch all categories for sidebar
        const categoriesRes = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/categories/with_counts/`
        );
        if (categoriesRes.ok) {
          const categoriesData = await categoriesRes.json();
          setCategories(categoriesData);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchData();
    }
  }, [slug]);

  // Effect para expandir automáticamente las categorías de nivel 0 con hijos
  useEffect(() => {
    if (categories.length > 0) {
      // Encontrar todas las categorías de nivel 0 que tengan hijos con artículos
      const levelZeroCategoriesWithChildren = categories.filter((category) => {
        const hasChildrenWithArticles =
          category.children &&
          category.children.some((child) => child.article_count > 0);
        return hasChildrenWithArticles;
      });

      // Extraer sus IDs
      const categoryIdsToExpand = levelZeroCategoriesWithChildren.map(
        (cat) => cat.id
      );

      // Establecer como expandidas
      setExpandedCategories(categoryIdsToExpand);
    }
  }, [categories]); // Se ejecuta cuando categories cambia

  // Función para toggle del acordeón
  const toggleCategory = (categoryId) => {
    setExpandedCategories((prev) =>
      prev.includes(categoryId)
        ? prev.filter((id) => id !== categoryId)
        : [...prev, categoryId]
    );
  };
  // ... (las funciones renderCategories, toggleCategory, getSortedArticles son iguales que en Home.jsx)
  // Función recursiva para renderizar categorías con filtro y acordeón
  const renderCategories = (categoriesList, level = 0) => {
    // Filtrar categorías con 1 o más artículos
    const filteredCategories = categoriesList.filter(
      (category) => category.article_count > 0
    );

    if (filteredCategories.length === 0) {
      return level === 0 ? (
        <p className="text-gray-500">No hay categorías con artículos</p>
      ) : null;
    }

    return filteredCategories.map((category) => {
      const hasChildren =
        category.children &&
        category.children.filter((c) => c.article_count > 0).length > 0;
      const isExpanded = expandedCategories.includes(category.id);

      return (
        <div key={category.id} className={`${level > 0 ? 'ml-4' : ''}`}>
          <div className="flex items-center py-1">
            {/* Ícono de expansión/contracción */}
            {hasChildren && (
              <button
                onClick={() => toggleCategory(category.id)}
                className="mr-1 w-4 h-4 flex items-center justify-center text-gray-500 hover:text-gray-700 transition-colors"
              >
                {isExpanded ? '−' : '+'}
              </button>
            )}

            {/* Espaciador para categorías sin hijos */}
            {!hasChildren && <span className="w-4 h-4 mr-1"></span>}

            {/* Enlace de categoría */}
            <Link
              to={`/category/${category.slug}`}
              className={`font-medium transition-colors ${
                category.slug === slug
                  ? 'text-[#003a5b] font-bold'
                  : 'text-[#2c72bc] hover:text-[#003a5b]'
              }`}
            >
              {category.name || category.slug} ({category.article_count})
            </Link>
          </div>

          {/* Children expandidos */}
          {hasChildren && isExpanded && (
            <div className="mt-1 ml-2 border-l-2 border-gray-200 pl-2">
              {renderCategories(category.children, level + 1)}
            </div>
          )}
        </div>
      );
    });
  };

  // Función para ordenar los artículos (igual que en Home.jsx)
  const getSortedArticles = (articles) => {
    const articlesToSort = [...articles];

    switch (sortOption) {
      case 'title_asc':
        return articlesToSort.sort((a, b) => a.title.localeCompare(b.title));
      case 'title_desc':
        return articlesToSort.sort((a, b) => b.title.localeCompare(a.title));
      case 'views_asc':
        return articlesToSort.sort((a, b) => a.views_count - b.views_count);
      case 'views_desc':
        return articlesToSort.sort((a, b) => b.views_count - a.views_count);
      case 'oldest':
        return articlesToSort.sort(
          (a, b) => new Date(a.published_at) - new Date(b.published_at)
        );
      case 'recent':
      default:
        return articlesToSort.sort(
          (a, b) => new Date(b.published_at) - new Date(a.published_at)
        );
    }
  };

  const sortedArticles = getSortedArticles(articles);

  // Opciones de ordenamiento (igual que en Home.jsx)
  const sortOptions = [
    { value: 'recent', label: 'Más recientes' },
    { value: 'oldest', label: 'Más antiguos' },
    { value: 'title_asc', label: 'Título (A-Z)' },
    { value: 'title_desc', label: 'Título (Z-A)' },
    { value: 'views_desc', label: 'Más vistos' },
    { value: 'views_asc', label: 'Menos vistos' },
  ];

  // Aplicar ordenamiento y paginación
  const startIndex = (currentPage - 1) * articlesPerPage;
  const endIndex = startIndex + articlesPerPage;
  const currentArticles = sortedArticles.slice(startIndex, endIndex);

  const totalPages = Math.ceil(sortedArticles.length / articlesPerPage);

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8 relative">
        {/* Spinner absoluto centrado */}
        <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-30 z-50">
          <div className="text-center">
            <div className="inline-flex items-center justify-center mb-4">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#0076ba] border-t-transparent"></div>
            </div>
            <p className="text-gray-600 font-medium text-lg">
              Cargando contenido...
            </p>
            <p className="text-gray-500 text-sm mt-1">Por favor espere</p>
          </div>
        </div>

        {/* Skeleton del header de categoría */}
        <div className="text-center mb-8 animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mx-auto mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/3 mx-auto"></div>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Contenido principal - Skeleton */}
          <div className="lg:w-2/3">
            <div className="flex justify-between items-center mb-6">
              <div className="h-6 bg-gray-200 rounded w-48"></div>
              <div className="h-10 bg-gray-200 rounded w-32"></div>
            </div>

            {/* Grid de artículos skeleton */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-white rounded-lg shadow">
                  <div className="h-48 bg-gray-200 rounded-t-lg"></div>
                  <div className="p-4">
                    <div className="h-5 bg-gray-200 rounded w-full mb-2"></div>
                    <div className="h-3 bg-gray-200 rounded w-3/4 mb-3"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                    <div className="flex justify-between items-center mt-4">
                      <div className="h-3 bg-gray-200 rounded w-24"></div>
                      <div className="h-6 bg-gray-200 rounded w-16"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Sidebar skeleton */}
          <div className="hidden lg:block lg:w-1/3 space-y-6">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="h-6 bg-gray-200 rounded w-32 mb-4"></div>
              <div className="space-y-2">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="h-4 bg-gray-200 rounded"></div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Función para extraer texto limpio del contenido HTML
  const cleanHtml = (html) => {
    if (!html) return '';
    return html.replace(/<[^>]*>/g, '').substring(0, 200) + '...';
  };

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header de la categoría */}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          {category?.name || 'Categoría'}
        </h1>
        {category?.description && (
          <p className="text-gray-600 max-w-2xl mx-auto">
            {category.description}
          </p>
        )}
        <div className="mt-4 text-sm text-gray-500">
          {articles.length} artículo(s) encontrado(s)
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-8">
        {/* Contenido principal - Ocupa 2/3 en desktop */}
        <div className="lg:w-2/3">
          <section className="my-10">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
              <h2 className="text-xl font-semibold">
                Artículos de la categoría
              </h2>

              {/* Selector de ordenamiento (igual que en Home.jsx) */}
              <div className="relative z-50">
                <button
                  onClick={() => setShowSortOptions(!showSortOptions)}
                  className="flex items-center space-x-2 bg-white border border-gray-300 rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <FunnelIcon className="h-4 w-4" />
                  <span>
                    {sortOptions.find((opt) => opt.value === sortOption)
                      ?.label || 'Ordenar por'}
                  </span>
                  <ChevronDownIcon
                    className={`h-4 w-4 transition-transform ${
                      showSortOptions ? 'rotate-180' : ''
                    }`}
                  />
                </button>

                {showSortOptions && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setShowSortOptions(false)}
                    />
                    <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-20">
                      {sortOptions.map((option) => (
                        <button
                          key={option.value}
                          onClick={() => {
                            setSortOption(option.value);
                            setShowSortOptions(false);
                            setCurrentPage(1);
                          }}
                          className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 transition-colors ${
                            sortOption === option.value
                              ? 'bg-blue-50 text-blue-600 font-medium'
                              : 'text-gray-700'
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Grid de artículos (igual que en Home.jsx) */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {currentArticles.map((a) => (
                <div
                  key={a.id}
                  className="relative bg-white rounded-lg shadow hover:shadow-lg transition"
                >
                  <Link to={`/${a.slug}`}>
                    {a.featured_image_url && (
                      <img
                        src={a.featured_image_url}
                        alt={a.title}
                        className="w-full h-48 object-cover rounded-t-lg"
                      />
                    )}
                    <div className="p-4">
                      {/* Categorías */}
                      <div className="flex flex-wrap gap-2 mb-3">
                        {a.categories
                          ?.flatMap((cat) =>
                            // Aplanar recursivamente y filtrar solo las que no tienen hijos
                            (function flattenLeaves(category) {
                              if (
                                category.children &&
                                category.children.length > 0
                              ) {
                                return category.children.flatMap(flattenLeaves);
                              }
                              return [category];
                            })(cat)
                          )
                          .map((category) => (
                            <span
                              key={category.id}
                              className="inline-block px-3 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full"
                            >
                              {category.name}
                            </span>
                          ))}
                      </div>
                      <h3 className="text-lg font-bold text-[#0076ba] hover:text-[#005a96] transition-colors">
                        {a.title}
                      </h3>
                      <p className="text-sm text-gray-600 my-3 line-clamp-3">
                        {cleanHtml(a.content)}
                      </p>
                      <p className="text-sm text-gray-600 mt-2">{a.excerpt}</p>
                      <small className="block mt-3 text-gray-500">
                        {a.author?.display_name} —{' '}
                        {new Date(a.published_at).toLocaleDateString('es-MX', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                        })}
                      </small>
                    </div>
                  </Link>
                </div>
              ))}
            </div>

            {/* Mensaje cuando no hay artículos */}
            {articles.length === 0 && (
              <div className="text-center py-8">
                <p className="text-gray-500">
                  No hay artículos en esta categoría.
                </p>
              </div>
            )}

            {/* Paginación (igual que en Home.jsx) */}
            {articles.length > 0 && (
              <div className="sticky bottom-0 left-0 right-0 bg-white py-4 shadow-lg rounded-lg z-50 border border-gray-200 mt-8">
                <div className="flex justify-evenly space-x-2">
                  <button
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(prev - 1, 1))
                    }
                    disabled={currentPage === 1}
                    className={`hidden sm:flex px-4 py-2 rounded-md ${
                      currentPage === 1
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-gray-200 hover:bg-gray-300'
                    }`}
                  >
                    ANTERIOR
                  </button>

                  <div className="flex space-x-2">
                    {/* Versión móvil: siempre 5 páginas consecutivas */}
                    <div className="sm:hidden flex space-x-2">
                      {(() => {
                        const mobilePages = [];
                        const mobileMaxPages = 6;
                        let mobileStart = Math.max(
                          1,
                          currentPage - Math.floor(mobileMaxPages / 2)
                        );
                        let mobileEnd = Math.min(
                          totalPages,
                          mobileStart + mobileMaxPages - 1
                        );

                        // Ajustar si estamos cerca del final
                        if (mobileEnd - mobileStart + 1 < mobileMaxPages) {
                          mobileStart = Math.max(
                            1,
                            mobileEnd - mobileMaxPages + 1
                          );
                        }

                        for (let i = mobileStart; i <= mobileEnd; i++) {
                          mobilePages.push(
                            <button
                              key={i}
                              onClick={() => setCurrentPage(i)}
                              className={`px-3 py-2 rounded-md text-sm ${
                                currentPage === i
                                  ? 'bg-[#0076ba] text-white'
                                  : 'bg-gray-200 hover:bg-gray-300'
                              }`}
                            >
                              {i}
                            </button>
                          );
                        }
                        return mobilePages;
                      })()}
                    </div>

                    {/* Versión desktop: con elipsis */}
                    <div className="hidden sm:flex space-x-2">
                      {(() => {
                        const desktopPages = [];
                        const desktopMaxPages = 3;
                        let desktopStart = Math.max(
                          1,
                          currentPage - Math.floor(desktopMaxPages / 2)
                        );
                        let desktopEnd = Math.min(
                          totalPages,
                          desktopStart + desktopMaxPages - 1
                        );

                        if (desktopEnd - desktopStart + 1 < desktopMaxPages) {
                          desktopStart = Math.max(
                            1,
                            desktopEnd - desktopMaxPages + 1
                          );
                        }

                        // Primera página
                        if (desktopStart > 1) {
                          desktopPages.push(
                            <button
                              key={1}
                              onClick={() => setCurrentPage(1)}
                              className={`px-4 py-2 rounded-md ${
                                currentPage === 1
                                  ? 'bg-[#0076ba] text-white'
                                  : 'bg-gray-300 hover:bg-gray-400'
                              }`}
                            >
                              1
                            </button>
                          );

                          if (desktopStart > 2) {
                            desktopPages.push(
                              <span key="ellipsis1" className="px-2 py-2">
                                ...
                              </span>
                            );
                          }
                        }

                        // Páginas visibles
                        for (let i = desktopStart; i <= desktopEnd; i++) {
                          desktopPages.push(
                            <button
                              key={i}
                              onClick={() => setCurrentPage(i)}
                              className={`px-4 py-2 rounded-md ${
                                currentPage === i
                                  ? 'bg-[#0076ba] text-white'
                                  : 'bg-gray-200 hover:bg-gray-300'
                              }`}
                            >
                              {i}
                            </button>
                          );
                        }

                        // Última página
                        if (desktopEnd < totalPages) {
                          if (desktopEnd < totalPages - 1) {
                            desktopPages.push(
                              <span key="ellipsis2" className="px-2 py-2">
                                ...
                              </span>
                            );
                          }

                          desktopPages.push(
                            <button
                              key={totalPages}
                              onClick={() => setCurrentPage(totalPages)}
                              className={`px-4 py-2 rounded-md ${
                                currentPage === totalPages
                                  ? 'bg-[#0076ba] text-white'
                                  : 'bg-gray-200 hover:bg-gray-300'
                              }`}
                            >
                              {totalPages}
                            </button>
                          );
                        }

                        return desktopPages;
                      })()}
                    </div>
                  </div>

                  <button
                    onClick={() =>
                      setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                    }
                    disabled={currentPage === totalPages}
                    className={`hidden sm:flex px-4 py-2 rounded-md ${
                      currentPage === totalPages
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-gray-200 hover:bg-gray-300'
                    }`}
                  >
                    SIGUIENTE
                  </button>
                </div>
              </div>
            )}
          </section>
        </div>

        {/* Sidebar - Ocupa 1/3 en desktop (igual que en Home.jsx) */}
        <div className="hidden lg:block lg:w-1/3">
          {/* Presentación de la empresa */}
          <section className="bg-white rounded-lg shadow p-6 my-8 mb-6">
            <div className="text-center">
              <img
                src="https://blog.packsys.com/wp-content/uploads/2012/01/logo.png"
                alt="Logo de la empresa"
                className="mx-auto mb-4 max-h-16"
              />
              <p className="text-gray-700">
                Somos una empresa creada hace más de 25 años y estamos enfocados
                a la fabricación y distribución de productos para empaque,
                embalaje e insumos industriales.
              </p>
            </div>
          </section>

          {/* Categorías */}
          <section className="bg-white rounded-lg shadow p-6 mb-6">
            <h3 className="text-lg font-semibold mb-4">Categorías</h3>
            <div className="space-y-1">
              {categories.length > 0 ? (
                renderCategories(categories)
              ) : (
                <p className="text-gray-500">Cargando categorías...</p>
              )}
            </div>
          </section>

          {/* Artículos Recientes */}
          <section className="bg-white rounded-lg shadow p-6 mb-8">
            <h3 className="text-lg font-semibold mb-4">Consultas Recientes</h3>
            <div className="space-y-3">
              {recentArticles.slice(0, 5).map((article) => (
                <Link
                  key={article.id}
                  to={`/${article.slug}`}
                  className="block hover:bg-gray-50 p-2 rounded transition-colors"
                >
                  <h6 className="text-[#2c72bc] hover:text-[#003a5b] transition-colors font-medium line-clamp-2">
                    {article.title}
                  </h6>
                  <small className="text-gray-500 italic text-sm">
                    {new Date(article.published_at).toLocaleDateString(
                      'es-ES',
                      {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      }
                    )}
                  </small>
                </Link>
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
